import { DateTime } from "luxon"
import { analyses, samples, sampleView } from "state_management"
import AddEditSamplePopup from "./AddEditSamplePopup"
import { useDispatch, useSelector } from "react-redux"
import { useClosePopup } from "components/Popup/useClosePopup"
import { useState, useLayoutEffect, useEffect, useMemo } from "react"
import { Popup } from "components"

import { chooseOptimalColor } from "utils/scripts/schedule"

import { FiCheck, FiFlag } from "react-icons/fi"
import { TbSquareLetterM } from "react-icons/tb"
import { BsCalendar2 } from "react-icons/bs"
import { AiOutlineExclamation } from "react-icons/ai"
import { HiOutlineArrowNarrowDown } from "react-icons/hi"
import { FaCircle } from "react-icons/fa"
import { FiCircle, FiAlertTriangle } from "react-icons/fi"
import useT from "utils/hooks/useT"


export default function SampleRowElement({
  group,
  maxWidth,
  activeSample,
  setActiveSample,
  samplePriority,
  setSamplePriority,
  filteredSampleGroups,
  setActiveSampleAnalysis,
  rowId,
}) {

  const dispatch = useDispatch()
  const { t } = useT("common")
  const [groupPriority, setGroupPriority] = useState(group.maxPriority)
  const timeZone = useSelector((state) => state.teamInfo.timezone)

  // Hook for a RefObject that checks if popup should close (only on outside click for priority dropdown)
  let domNode = useClosePopup(() => {
    setActiveSample(null)
  }, { closeOnEsc: false, closeOnOutsideClick: true })

  // Measure dropdown position when opened and flip it upward if it would overflow the viewport
  const [dropUp, setDropUp] = useState(false)

  useLayoutEffect(() => {
    if (!domNode || !domNode.current) return
    // Only run when this group's dropdown is active
    if (activeSample !== rowId) {
      setDropUp(false)
      return
    }
    const node = domNode.current

    const trigger = node.parentElement ? node.parentElement.getBoundingClientRect() : node.getBoundingClientRect()
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight

    const HEADER_HEIGHT = 36
    const ITEM_HEIGHT = 38
    const ITEM_COUNT = 3
    const GAP = 8

    const contentHeight = HEADER_HEIGHT + ITEM_COUNT * ITEM_HEIGHT
    const spaceBelow = viewportHeight - trigger.bottom
    const shouldDropUp = spaceBelow < contentHeight + GAP
    if (shouldDropUp !== dropUp) setDropUp(shouldDropUp)
  }, [activeSample, rowId, domNode, dropUp])

  const prioNumberMeaning = {1: t("priorityHigh"), 2: t("priorityMedium"), 3: t("priorityLow")}

  // Show edit popup for manual sample groups
  const [showEditPopup, setShowEditPopup] = useState(false)
  const [selectedSampleForEdit, setSelectedSampleForEdit] = useState(null)

  // Select raw slices, then memoize derived collections so the selector returns
  // stable references (prevents unnecessary rerenders / selector warnings).
  const analysisDefs = useSelector((state) => state.analysisDefinitions)
  const sampleGroupsFromState = useSelector((state) => state.sampleView.sampleGroups)

  const analysisCollection = useMemo(() => {
    if (!analysisDefs) return []
    return Object.values(analysisDefs).map((a) => ({
      id: a.analysisId || a.id || a.name,
      name: a.display_name || a.displayName || a.name,
    }))
  }, [analysisDefs])

  const productCollection = useMemo(() => {
    const set = new Set()
    ;(sampleGroupsFromState || []).forEach((s) => set.add(s.productName || ""))
    return Array.from(set).map((p) => ({ id: p, name: p === "" ? t("samplesWithoutProduct") : p }))
  }, [sampleGroupsFromState, t])

  // Keep local priority in sync with prop updates (so when store updates, all rows update)
  useEffect(() => {
    if (group.maxPriority !== groupPriority) setGroupPriority(group.maxPriority)
  }, [group.maxPriority, groupPriority])

  // Memoize the selected sample to prevent it from changing on every render
  const selected = useMemo(() => {
    return selectedSampleForEdit || {}
  }, [selectedSampleForEdit])

  // Compute initial values for the popup from the selected sample (with fallbacks to group)
  const initialSampleName = selected.sampleName || selected.sample_name || group.sampleName || ""
  const initialObservationId = selected.observationId || selected.observation_id || group.observationId
  const initialArrival = selected.arrival || group.arrival || group.maxArrival || ""
  const initialDeadline = selected.deadline || group.deadline || group.minDeadline || ""
  const initialPriority = selected.priority || selected.maxPriority || group.maxPriority || 2
  const initialStatus = selected.status || group.status || "TODO"
  const initialProductName = selected.productName || group.productName || ""
  const initialCustomerName = selected.customerName || selected.customer_name || group.customerName || group.customer_name || ""
  const initialExclusive = typeof selected.exclusive !== 'undefined' ? selected.exclusive : group.exclusive
  const initialIsFlagged = typeof selected.isFlagged !== 'undefined' ? selected.isFlagged : group.isFlagged

  // Get existing analyses on the sample to pass for filtering
  const initialAnalysisIds = useMemo(() => {
    if (!selected || !group.analyses) return []
    
    // Get all analyses from the group that match this sample's observationId
    const sampleObservationId = selected.observationId || selected.observation_id
    const sampleAnalyses = group.analyses.filter(a => 
      String(a.observationId || a.observation_id) === String(sampleObservationId)
    )
    
    // Map to analysis IDs
    return sampleAnalyses.map(a => {
      // Try to find the matching analysis in analysisCollection to get the canonical ID
      const foundByName = analysisCollection.find(ac => String(ac.name) === String(a.name || a.analysis_name))
      if (foundByName) return foundByName.id
      
      const foundById = analysisCollection.find(ac => String(ac.id) === String(a.analysisId || a.analysis_id || a.id))
      if (foundById) return foundById.id
      
      return a.analysisId || a.analysis_id || a.id
    }).filter(Boolean)
  }, [selected, group.analyses, analysisCollection])

  return (
    <div className="samplegroup-row" key={group.sampleName}>
      <h3
        style={{ width: maxWidth }}
        title={`${group.sampleName}\n${t("sampleIdWithValue", { id: group.observationId })}${initialCustomerName ? `\n${t("customerName")}: ${initialCustomerName}` : ""}`}
        onClick={() => {
          // Determine candidate sample objects (some backends use group.samples, older code used group.analyses)
          const candidates = Array.isArray(group.samples) && group.samples.length > 0 ? group.samples : (group.analyses || [])

          // Try to find a candidate matching this observation id
          const matching = candidates.find((s) => String(s.observationId || s.observation_id) === String(group.observationId))

          // Open edit popup only if the targeted sample is manual
          const targetSampleForEdit = matching || candidates.find((s) => s.manual === true) || null
          if (targetSampleForEdit && targetSampleForEdit.manual === true) {
            setSelectedSampleForEdit(targetSampleForEdit)
            setShowEditPopup(true)
            return
          }
          setActiveSample(activeSample === rowId ? null : rowId)
          const baseName = group.baseSampleName || group.sampleName
          const found = filteredSampleGroups.find((obj) => obj.sampleName === baseName)
          if (found) setSamplePriority(found.maxPriority)
        }}
      >
        {(() => {
          const candidates = Array.isArray(group.samples) && group.samples.length > 0 ? group.samples : (group.analyses || [])
          const hasManual = candidates.find((s) => s && (s.manual === true || String(s.manual) === 'true'))
          if (hasManual) {
            return (
              <div className="manual-sample-badge" title={t("manualSample")}>
                <TbSquareLetterM />
              </div>
            )
          }
          return null
        })()}

        <span className="sampleDeadline">
          {DateTime.fromISO(group.minDeadline, { zone: timeZone }).toFormat("dd/MM")}
        </span>
        <span className="sampleName">{group.sampleName}</span>
        <span className="sampleIcons">
          {groupPriority === 1 && <AiOutlineExclamation title={t("sampleHighestPriority")} />}
          {groupPriority === 3 && <HiOutlineArrowNarrowDown title={t("sampleLowestPriority")} />}
          {group.isDone && <FiCheck title={t("sampleIsDone")} />}
        </span>
        {group.analyses && group.analyses.some(analysis => analysis.shouldBeDone) && (<FiAlertTriangle className="alert-triangle-sample" title={t("sampleInProgressOnDoneAnalysis")} />)}
        {activeSample === rowId && (
          <div className={`sampleview-dropdown ${dropUp ? 'dropup' : ''}`} ref={domNode}>
            <div className="sampleview-dropdown-content">
              <header className="sampleview-dropdown-header">{t("priority")}</header>
              {[1, 2, 3].map((priority) => {
                return (
                  <div
                    key={priority}
                    className="sampleview-dropdown-item"
                    onClick={() => {
                      if (group.maxPriority !== priority) {
                        setGroupPriority(priority)
                        // Use the base sample name when dispatching priority change
                        const baseName = group.baseSampleName || group.sampleName
                        const observationId = group.observationId
                        const previous = group.maxPriority
                        // Optimistically update the store so all rows update immediately
                        dispatch({ type: "samples/changePriority/OPTIMISTIC", args: [priority, baseName, observationId] })
                        dispatch(samples.changePriority(priority, baseName, observationId)).then (() => {
                          // refresh unplanned analyses; actual store already updated optimistically
                          dispatch(analyses.fetchUnplanned())
                        }).catch((err) => {
                          // Revert optimistic update if API fails
                          dispatch({ type: "samples/changePriority/REVERT", args: [previous, baseName, observationId] })
                        })
                      }
                    }}
                  >
                    {priority + " " + prioNumberMeaning[priority]}{" "}
                    {groupPriority === priority ? (
                      <FaCircle style={{ width: "0.75rem", height: "0.75rem" }} />
                    ) : (
                      <FiCircle
                        style={{
                          width: "0.75rem",
                          height: "0.75rem",
                          color: "white",
                          backgroundColor: "white",
                          borderRadius: "50%",
                        }}
                      />
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        )}
      </h3>
      <div className="samplerow-analyses-grouped">
        {group.analyses.map((analysis, idx) => (
          <div
            className="samplerow-analysis"
            key={idx}
            style={{ backgroundColor: analysis.color, color: chooseOptimalColor(analysis.color), justifyContent: analysis.shouldBeDone ? 'space-between' : ''}}
            title={analysis.name + "\n" + t("sampleIdWithValue", { id: analysis.observationId })}
            onClick={(event) => {
              // Use the analysis directly from the group instead of searching for it
              let foundAnalysis = analysis

              if (foundAnalysis && foundAnalysis.analysesPlanned) {
                foundAnalysis.tasks.sort((a, b) => DateTime.fromISO(a.end) - DateTime.fromISO(b.end))
              }

              const buttonRect = event.target.getBoundingClientRect();
              foundAnalysis.position = ({
                top: buttonRect.top + (1.5*buttonRect.height),
                left: buttonRect.left + (buttonRect.width / 2),
                height: buttonRect.height,
              })
              const baseName = group.baseSampleName || group.sampleName
              const customerName =
                foundAnalysis?.customerName ||
                foundAnalysis?.customer_name ||
                group.customerName ||
                group.customer_name ||
                null
              setActiveSampleAnalysis({ ...foundAnalysis, sampleName: baseName, productName: group.productName, customerName })
            }}
          >
            {analysis.shouldBeDoneToday && analysis.status === "DONE" ? (
              <FiCheck title={t("analysisAndSampleDone")} size="1.5em" />
            ) : analysis.shouldBeDoneToday ? (
                <FiCheck title={t("analysisDone")} size="1.5em" />
              ) : analysis.status === "DONE" ? (
                  <FiCheck title={t("sampleIsDone")} size="1.5em" />
                ) : analysis.analysesPlanned ? (
                    <BsCalendar2 title={t("analysisPlanned")} size="1.2em" />
                  ) : (
                      ""
                    )}
            <span style={{pointerEvents: 'none'}}>{analysis.name}</span>
            {analysis.shouldBeDone && (<FiAlertTriangle className="alert-triangle-analysis" title={t("sampleInProgressOnDoneAnalysis")} />)}
            {analysis.isFlagged === true && (<FiFlag className="flag-analysis" title={t("analysisIsFlagged")} />)}
          </div>
        ))}
      </div>
      {showEditPopup && (
        <Popup onCancel={() => { setShowEditPopup(false); setSelectedSampleForEdit(null) }}>
        <AddEditSamplePopup
          title={t("editSampleName", { name: initialSampleName })}
          initial={{
            sampleName: initialSampleName,
            analysisName: initialAnalysisIds, // Pass existing analyses so they can be filtered out
            observationId: initialObservationId,
            arrival: initialArrival,
            deadline: initialDeadline,
            priority: initialPriority,
            status: initialStatus,
            productName: initialProductName,
            exclusive: initialExclusive,
            isFlagged: initialIsFlagged,
            numberOfObservations: selected.numberOfObservations || undefined,
          }}
          analysisCollection={analysisCollection}
          productCollection={productCollection}
          isEdit={true}
          onCancel={() => { setShowEditPopup(false); setSelectedSampleForEdit(null) }}
          onSubmit={(payload) => {
            const sampleId = selectedSampleForEdit ? (selectedSampleForEdit.sampleId || selectedSampleForEdit.id) : null
            if (!sampleId) {
              setShowEditPopup(false)
              setSelectedSampleForEdit(null)
              return
            }
            dispatch(sampleView.editSampleGroup(sampleId, payload)).then(() => {
              setShowEditPopup(false)
              setSelectedSampleForEdit(null)
            }).catch(() => {
              // keep popup open to let user retry
            })
          }}
        />
        </Popup>
      )}
    </div>
  )
}
